import type { KyInstance } from "ky";
import { AnalyticsApi } from "./analytics.js";
import { ApplicationApi } from "./applications.js";
import { InquiryApi } from "./inquiries.js";
import {
  InvitationApi,
  type AcceptInvitationRequest,
  type InvitationPreview,
} from "./invitations.js";
import { NewsletterApi } from "./newsletter.js";
import { PractitionerPublicApi } from "./practitioners-public.js";
import { PractitionerApi } from "./practitioners.js";
import { AdminTaxonomyApi, TaxonomyApi } from "./taxonomy.js";
import { UserApi } from "./users.js";
import { AuthApi } from "./auth.js";
import type { AccountType } from "./auth.js";
export * from "./auth.js";

export type UserRole = "owner" | "staff";

export type InvitableRole = "owner" | "staff";

/** A way an account can sign in. Mirrors the `provider` CHECK on `auth_identities`. */
export type AuthProvider = "password" | "google";

export interface Credentials {
  email: string;
  password: string;
}

export interface GoogleExchangeRequest {
  code: string;
  state: string;
}

export interface ConfirmEmailVerificationRequest {
  code: string;
}

export interface RequestPasswordResetRequest {
  email: string;
  accountType: AccountType;
}

export interface VerifyPasswordResetRequest {
  email: string;
  code: string;
  accountType: AccountType;
}

/** Step two's product: a single-use credential for `ConfirmPasswordResetRequest`. */
export interface VerifyPasswordResetResponse {
  resetToken: string;
  expiresAt: string;
}

export interface ConfirmPasswordResetRequest {
  resetToken: string;
  newPassword: string;
}

/** Practitioner-only by construction: there is deliberately no `accountType`. */
export interface RequestSignInCodeRequest {
  email: string;
}

export interface VerifySignInCodeRequest {
  email: string;
  code: string;
}

export interface ConfirmPasswordResetResponse {
  message: string;
}

export interface SetPasswordRequest {
  /** Required only when the account already has a password. */
  currentPassword?: string;
  newPassword: string;
}

export interface MessageResponse {
  message: string;
}

export * from "./address.js";
export * from "./analytics.js";
export * from "./applications.js";
export * from "./inquiries.js";
export * from "./invitations.js";
export * from "./newsletter.js";
export * from "./pagination.js";
export * from "./practitioners-public.js";
export * from "./practitioners.js";
export * from "./taxonomy.js";
export * from "./turnstile.js";
export * from "./users.js";

export class CmwClient {
  readonly auth: AuthApi;
  readonly invitations: InvitationApi;
  readonly inquiries: InquiryApi;
  readonly newsletter: NewsletterApi;
  readonly taxonomy: TaxonomyApi;
  readonly taxonomyAdmin: AdminTaxonomyApi;
  readonly applications: ApplicationApi;
  readonly practitioners: PractitionerApi;
  readonly practitionersPublic: PractitionerPublicApi;
  readonly analytics: AnalyticsApi;
  readonly users: UserApi;

  constructor(http: KyInstance) {
    this.auth = new AuthApi(http);
    this.invitations = new InvitationApi(http);
    this.inquiries = new InquiryApi(http);
    this.newsletter = new NewsletterApi(http);
    this.taxonomy = new TaxonomyApi(http);
    this.taxonomyAdmin = new AdminTaxonomyApi(http);
    this.applications = new ApplicationApi(http);
    this.practitioners = new PractitionerApi(http);
    this.practitionersPublic = new PractitionerPublicApi(http);
    this.analytics = new AnalyticsApi(http);
    this.users = new UserApi(http);
  }
}

export const COOKIE_NAME_BY_ACCOUNT_TYPE: Record<AccountType, string> = {
  admin: "admin_session_token",
  practioner: "practitioner_session_token",
}
